<template>
    <div class="dokan-quote-wrapper">
        <tabs />
        <br />
        <h1 class="wp-heading-inline">{{ __('Quotes', 'dokan') }}</h1>
        <router-link :to="{ name: 'NewRequestQuote' }" class="page-title-action">{{ __('New Quote', 'dokan') }}</router-link>

        <hr class="wp-header-end">

        <ul class="subsubsub">
            <li><router-link :to="{ name: 'RequestAQuote' }" active-class="current" exact>{{ __( 'All', 'dokan' ) }} <span class="count">{{ counts.all }}</span></router-link><span class='pipe'>|</span></li>
            <li v-for="( quoteStatus, index ) in statusList" :key="quoteStatus.key">
                <router-link :to="{ name: 'RequestAQuote', query: { status: quoteStatus.key } }" active-class='current' exact>
                    {{ quoteStatus.label }} <span class="count">{{ counts[ quoteStatus.key ] }} </span>
                </router-link>
                <span class='pipe' v-if="index < statusList.length - 1">|</span>
            </li>
        </ul>

        <list-table
            :columns="columns"
            :rows="requests"
            :loading="loading"
            :action-column="actionColumn"
            :actions="actions"
            :show-cb="showCb"
            :bulk-actions="bulkActions"
            :not-found="notFound"
            :total-pages="totalPages"
            :total-items="totalItems"
            :per-page="perPage"
            :current-page="currentPage"
            @pagination="goToPage"
            @bulk:click="onBulkAction"
            :text="$root.listTableTexts()"
        >
            <template slot="sl" slot-scope="data">
                <strong v-if="'trash' === data.row.status">{{ __('Quote', 'dokan') + ' ' + data.row.id }}</strong>
                <strong v-else><a :href="editUrl(data.row.id)">{{ __('Quote', 'dokan') + ' ' + data.row.id }}</a></strong>
            </template>

            <template slot="title" slot-scope="data">
                <strong>{{ data.row.title }}</strong>
            </template>

            <template slot="status" slot-scope="data">
                <span :class="data.row.status">
                    {{ statusList.find( item => item.key === data.row.status )?.label }}
                </span>
            </template>

            <template slot="created_at" slot-scope="data">
                {{ data.row.created_at }}
            </template>

            <template slot="row-actions" slot-scope="data">
                <template v-if="data.row.status !=='converted'" v-for="(action, index) in actions">
                    <span :class="action.key" v-if="action.key === 'edit'">
                        <a :href="editUrl(data.row.id)">{{ action.label }}</a>
                        <template v-if="index !== ( actions.length - 1)"> | </template>
                    </span>
                    <span :class="action.key" v-if="action.key === 'trash' && currentStatus !=='trash'">
                        <a href="#" @click.prevent="rowAction( action.key, data )">{{ action.label }}</a>
                    </span>
                    <span :class="action.key" v-if="action.key === 'delete' && currentStatus === 'trash'">
                        <a href="#" @click.prevent="rowAction( action.key, data )">{{ action.label }}</a>
                        <template v-if="index !== ( actions.length - 1)"> | </template>
                    </span>
                    <span :class="action.key" v-if="action.key === 'restore' && currentStatus === 'trash'">
                        <a href="#" @click.prevent="rowAction( action.key, data )">{{ action.label }}</a>
                        <template v-if="index !== ( actions.length - 1)"> | </template>
                    </span>
                </template>
            </template>

            <template slot="filters">
                <div id="rfq-filters">
                    <date-range-picker
                        class="mr-2"
                        ref="picker"
                        :locale-data="datePickerFormat()"
                        :singleDatePicker="false"
                        :timePicker="false"
                        :timePicker24Hour="false"
                        :showWeekNumbers="false"
                        :showDropdowns="false"
                        :autoApply="false"
                        v-model="dateRange"
                        @update="dateRangeUpdated"
                        :linkedCalendars="true"
                        opens="center"
                    >
                        <template v-slot:input="picker">
                            <span v-if="dateRange.from_date">{{ dateRange.from_date | date }} - {{ dateRange.to_date | date }}</span>
                            <span class="date-range-placeholder" v-if="! dateRange.from_date">{{ __( 'Filter by date', 'dokan' ) }}</span>
                        </template>

                        <div slot="footer" slot-scope="data" class="drp-buttons">
                            <span class="drp-selected">{{ data.rangeText }}</span>
                            <button @click="clearDateRange()" type="button" class="cancelBtn btn btn-sm btn-secondary">{{ __( 'Clear', 'dokan' ) }}</button>
                            <button @click="data.clickApply" v-if="!data.in_selection" type="button" class="applyBtn btn btn-sm btn-success">{{ __( 'Apply', 'dokan' ) }}</button>
                        </div>
                    </date-range-picker>

                    <div id="rfq-filter-queries">
                        <multiselect
                            id="ajax"
                            label="name"
                            track-by="id"
                            autocomplete="off"
                            :read-only="true"
                            :searchable="true"
                            :options="customers"
                            :hide-selected="true"
                            open-direction="bottom"
                            :clear-on-select="true"
                            :close-on-select="true"
                            @select="onSelectCustomer"
                            v-model="selected_customer"
                            @search-change="asyncCustomerFind"
                            :showLabels="false"
                            :placeholder="__( 'Filter by customer', 'dokan' )"
                        ></multiselect>
                    </div>
                    <div class="filter-buttons">
                        <button
                            type="button"
                            class="button ml-2"
                            v-if="selected_customer"
                            @click="clearFilterQuote"
                        >&times;</button>
                        <button class="button" @click="filterQuoteList">
                            {{ __( 'Filter', 'dokan' ) }}
                        </button>
                    </div>
                </div>
            </template>
        </list-table>
    </div>
</template>

<script>
import Tabs from '../components/Tabs.vue'

const apiBase         = 'request-for-quote';
const Debounce        = dokan_get_lib( 'debounce' );
const ListTable       = dokan_get_lib( 'ListTable' );
const Multiselect     = dokan_get_lib( 'Multiselect' );
const DateRangePicker = dokan_get_lib( 'DateRangePicker' );

export default {
    name: 'RequestAQuote',

    components: {
        Tabs,
        ListTable,
        Multiselect,
        DateRangePicker
    },

    data () {
        return {
            requests: [],
            customers: [],
            dateRange: {
                from_date: '',
                to_date: '',
            },
            loading: false,
            counts: {
                all: 0,
                publish: 0,
                approve: 0,
                reject: 0,
                cancel: 0,
                expired: 0,
                draft: 0,
                future: 0,
                pending: 0,
                converted: 0,
                trash: 0,
            },
            notFound: this.__('No quote found.', 'dokan'),
            totalPages: 1,
            perPage: 10,
            totalItems: 0,
            showCb: true,
            actionColumn: 'sl',
            actions: [
                {
                    key: 'edit',
                    label: this.__('Edit', 'dokan'),
                },
                {
                    key: 'trash',
                    label: this.__('Trash', 'dokan'),
                },
                {
                    key: 'delete',
                    label: this.__('Delete Permanently', 'dokan'),
                },
                {
                    key: 'restore',
                    label: this.__('Restore', 'dokan'),
                },
            ],
            selected_date: 'all',
            selected_customer: '',
            customer_info: {
                name_field: '',
                email_field: '',
                customer_id: '',
                phone_field: '',
            },
        }
    },

    filters: {
        date(date) {
            return date ? moment( date ).format( 'MMMM D, YYYY' ) : '';
        }
    },

    watch: {
        '$route.query.status' () {
            this.fetchAll()
        },

        '$route.query.page' () {
            this.fetchAll()
        },
    },

    computed: {
        currentStatus () {
            return this.$route.query.status || 'all'
        },
        currentPage () {
            let page = this.$route.query.page || 1
            return parseInt(page)
        },
        bulkActions () {
            if ('trash' === this.$route.query.status) {
                return [
                    {
                        key: 'delete',
                        label: this.__('Delete Permanently', 'dokan'),
                    },
                    {
                        key: 'restore',
                        label: this.__('Restore', 'dokan'),
                    },
                ]
            } else {
                return dokan?.request_quote?.status_list;
            }
        },
        checkLangRtlHelp () {
            return isRtl === 1 && isRtl !== undefined ? 'help-block-rtl' : 'help-block-ltr'
        },
        statusList() {
            const statusList = [
                { key: 'pending',   label: this.__( 'Pending', 'dokan' ) },
                { key: 'approve',   label: this.__( 'Approved', 'dokan' ) },
                { key: 'updated',   label: this.__( 'Updated', 'dokan' ) },
                { key: 'accepted',  label: this.__( 'Accepted', 'dokan' ) },
                { key: 'expired',   label: this.__( 'Expired', 'dokan' ) },
                { key: 'reject',    label: this.__( 'Rejected', 'dokan' ) },
                { key: 'cancel',    label: this.__( 'Cancelled', 'dokan' ) },
                { key: 'converted', label: this.__( 'Converted', 'dokan' ) },
                { key: 'draft',     label: this.__( 'Draft', 'dokan' ) },
                { key: 'trash',     label: this.__( 'Trash', 'dokan' ) },
            ];

            return dokan.hooks.applyFilters( 'dokan_rfq_admin_status_list', statusList, this.counts );
        },
        columns() {
            let columns = {
                sl            : { label: this.__( 'Quote #', 'dokan' ) },
                customer_name : { label: this.__( 'Customer', 'dokan' ) },
                status        : { label: this.__( 'Status', 'dokan' ) },
                store_name    : { label: this.__( 'Vendor', 'dokan' ) },
                created_at    : { label: this.__( 'Date', 'dokan' ) },
            };

            return dokan.hooks.applyFilters( 'dokan_rfq_admin_column_list', columns );
        },
    },

    methods: {

        updatedCounts (xhr) {
            this.counts.all = parseInt(xhr.getResponseHeader('X-Status-All'))
            this.counts.publish = parseInt(xhr.getResponseHeader('X-Status-Publish'))
            this.counts.draft = parseInt(xhr.getResponseHeader('X-Status-Draft'))
            this.counts.updated = parseInt(xhr.getResponseHeader('X-Status-Updated'))
            this.counts.accepted = parseInt(xhr.getResponseHeader('X-Status-Accepted'))
            this.counts.expired = parseInt(xhr.getResponseHeader('X-Status-Expired'))
            this.counts.reject = parseInt(xhr.getResponseHeader('X-Status-Rejected'))
            this.counts.cancel = parseInt(xhr.getResponseHeader('X-Status-Cancelled'))
            this.counts.pending = parseInt(xhr.getResponseHeader('X-Status-Pending'))
            this.counts.future = parseInt(xhr.getResponseHeader('X-Status-Future'))
            this.counts.converted = parseInt(xhr.getResponseHeader('X-Status-Converted'))
            this.counts.approve = parseInt(xhr.getResponseHeader('X-Status-Approved'))
            this.counts.trash = parseInt(xhr.getResponseHeader('X-Status-Trash'))
        },

        updatePagination (xhr) {
            this.totalPages = parseInt(xhr.getResponseHeader('X-WP-TotalPages'))
            this.totalItems = parseInt(xhr.getResponseHeader('X-WP-Total'))
        },

        fetchAll () {
            this.loading = true;
            const data = {
                per_page: this.perPage,
                page: this.currentPage,
                date: this.dateRange,
                status: this.currentStatus,
                customer: this.selected_customer?.name,
            }
            dokan.api.get('/' + apiBase, data).done((response, status, xhr) => {
                this.requests = response
                this.loading = false

                this.updatedCounts(xhr)
                this.updatePagination(xhr)
            })
        },

        moment (date) {
            return moment(date)
        },

        editUrl (id) {
            return dokan.urls.adminRoot + 'admin.php?page=dokan#/' + apiBase + '/' + id + '/edit'
        },

        goToPage (page) {
            this.$router.push({
                name: 'RequestAQuote',
                query: {
                    status: this.currentStatus,
                    page: page,
                },
            })
        },

        rowAction (action, data) {
            if (!data.row.id) {
                alert(this.__('No data found', 'dokan'))
                return
            }

            if ('trash' === action || 'delete' === action) {
                this.loading = true

                var isPermanentDelete = ('delete' === action) ? '?force=true' : ''

                dokan.api.delete('/' + apiBase + '/' + data.row.id + isPermanentDelete).done((response, status, xhr) => {
                    this.fetchAll()
                    this.loading = false
                }).error((response, status, xhr) => {
                    this.loadSpinner = false
                    alert(response.responseJSON.message)
                })
            }

            if ('restore' === action) {
                this.loading = true

                dokan.api.put('/' + apiBase + '/' + data.row.id + '/restore').done((response, status, xhr) => {
                    this.fetchAll()
                    this.loading = false
                }).error((response, status, xhr) => {
                    this.loadSpinner = false
                    alert(response.responseJSON.message)
                })
            }
        },

        onBulkAction ( action, items ) {
            const jsonData = { action, items };
            dokan.api.put( '/' + apiBase + '/batch', jsonData ).done( ( response ) => {
                this.fetchAll()
                this.loading = false;
            }).error( ( response ) => {
                this.loadSpinner = false;
                alert( response.responseJSON.message );
            })
        },

        asyncCustomerFind: Debounce( function( query = '' ) {
            this.isLoading = true
            if ( query.length < 1 ) {
                return;
            }

            this.customers = _.map( this.requests, function( item ) {
                return {
                    id   : item.id,
                    name : item.customer_name,
                };
            } ).filter( ( value, index, self ) =>
                index === self.findIndex( ( t ) => (
                    t.name === value.name
                ) )
            );
        }, 300 ),

        onSelectCustomer( customer ) {
            this.customer_info = {
                'customer_id' : customer?.id,
                'name_field'  : customer?.name,
            };
        },

        filterQuoteList() {
            this.fetchAll();
        },

        clearFilterQuote() {
            this.selected_customer = 0;
            this.fetchAll();
        },

        datePickerFormat() {
            if (this.dateTimePickerFormat) {
                return this.dateTimePickerFormat();
            }
            return {
                format: dokan_get_daterange_picker_format().toLowerCase(),
                separator: ' - ',
                applyLabel: this.__( 'Apply', 'dokan' ),
                cancelLabel: this.__( 'Clear', 'dokan' ),
            }
        },

        dateRangeUpdated() {
            this.dateRange.from_date = moment( this.dateRange.startDate ).format( 'YYYY-MM-DD' );
            this.dateRange.to_date = moment( this.dateRange.endDate ).format( 'YYYY-MM-DD' );
        },

        clearDateRange() {
            this.dateRange.from_date = '';
            this.dateRange.to_date = '';
            this.$refs.picker.togglePicker(false);
        },
    },

    created () {
        this.fetchAll();
    },

}

</script>

<style lang="less">
.dokan-quote-wrapper {
    position: relative;

    .help-block {
        position: absolute;
        top: 10px;

        span.help-text {
            display: inline-block;
            margin-top: 4px;
            margin-right: 6px;

            a {
                text-decoration: none;
            }
        }

        span.dashicons {
            font-size: 25px;
        }
    }

    .alignleft.actions {
        #rfq-filters {
            display: flex;
            max-height: 30px;

            #rfq-filter-queries {
                display: flex;
                padding-right: 8px;
            }

            .filter-buttons {
                gap: 8px;
                display: flex;
            }
        }
    }

    th.title {
        width: 35%;
    }

    td.status {
        span {
            line-height: 2.5em;
            padding: 5px 8px;
            border-radius: 4px;
        }

        .publish {
            background: #c6e1c6;
            color: #5b841b;
        }

        .approve,
        .accepted {
            background: #c0e1f8;
            color: #1b6384;
        }

        .approve {
            display: inline;
        }

        .converted {
            background: #46c994;
            color: #ffffff;
        }

        .pending {
            background: #f8dda7;
            color: #94660c;
        }

        .trash,
        .cancel,
        .reject {
            background: #eba3a3;
            color: #761919;
        }

        .draft {
            background: #e5e5e5;
            color: #000;
        }

        .expired {
            background: #64748b;
            color: #fff;
        }

        .updated {
            background: #fed7aa;
            color: #7c2d12;
        }

        .future {
            background: #8080805c;
            color: #000000e0;
        }
    }

    .dokan-modal {
        header.modal-header {
            h1 {
                width: 90%;
            }
        }
    }

    .announcement-modal-content {
        height: 200px;
    }
}

@media only screen and (max-width: 600px) {
    .dokan-quote-wrapper {
        .help-block {
            top: 45px !important;
            left: 0 !important;
        }

        .subsubsub {
            display: flex;
            margin-top: 20px;

            li {
                display: flex;
                align-items: center;
            }
        }

        table {
            td.title, td.content {
                display: table-cell !important;
            }

            th:not(.check-column):not(.title):not(.content):not(.send_to) {
                display: none;
            }

            td:not(.check-column):not(.title):not(.content):not(.send_to) {
                display: none;
            }

            th.column, td.column {
                width: auto;
            }

            td.manage-column.column-cb.check-column {
                padding-right: 15px;
            }
        }
    }
}


.help-block-rtl {
    left: 10px;
}

.help-block-ltr {
    right: 10px;
}

span.pipe {
    margin-right: 3px;
}
</style>
